import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, api, type PlaybackSources } from '../api';
import { iframeEmbedCode, scriptEmbedCode } from '../embedCode';
import { videoFrameStyle } from '../videoLayout';
import { DEFAULT_PLAYER_SETTINGS, WATERMARK_POSITIONS, validPlayerColor } from '../playerSettings';
import type { EmbedPolicy, PlayerSettings, VideoItem } from '../types';
import { formatBytes, formatDate, formatDuration } from '../utils';
import { Icon } from './Icon';
import { Modal } from './Modal';
import { StatusBadge } from './StatusBadge';
import { CaptionTracksPanel } from './CaptionTracksPanel';
import { ChaptersPanel } from './ChaptersPanel';
import { VideoAnalyticsPanel } from './VideoAnalyticsPanel';
import { getBranding } from '../branding';
import { PublicSharesPanel } from './PublicSharesPanel';
import { MediaDetails } from './MediaDetails';

type DetailTab = 'overview' | 'captions' | 'chapters' | 'analytics' | 'embed' | 'share';
type TemporaryAsset = 'hls' | 'mp4' | 'thumbnail';

interface VideoPreviewProps {
  publicSharePagesEnabled?: boolean;
  memberPublicBrandingEnabled?: boolean;
  sharingReadOnly?: boolean;
  video: VideoItem | null;
  canEdit: boolean;
  folderName: string;
  onClose: () => void;
  onCopied: (message: string) => void;
  onThumbnail: (video: VideoItem) => void;
  onMove: (video: VideoItem) => void;
  onDelete: (video: VideoItem) => void;
  onRetry: (video: VideoItem) => void;
  onUpdated: (video: VideoItem) => void;
}

interface CopyCodeFieldProps {
  label: string;
  value: string;
  onCopy: () => void;
}

function CopyCodeField({ label, value, onCopy }: CopyCodeFieldProps) {
  return <label className="video-details__code-field">
    <span>{label}</span>
    <div>
      <textarea aria-label={label} value={value} readOnly onFocus={(event) => event.currentTarget.select()} />
      <button type="button" className="button button--secondary" onClick={onCopy}><Icon name="clipboard" size={16} />Copy</button>
    </div>
  </label>;
}

interface AssetRowProps {
  label: string;
  description: string;
  value?: string | null;
  busy?: boolean;
  onCopy: () => void;
}

function AssetRow({ label, description, value, busy = false, onCopy }: AssetRowProps) {
  return <div className="video-details__asset-row">
    <div><strong>{label}</strong><small>{description}</small>{value && <code>{value}</code>}</div>
    <button type="button" className="icon-button" onClick={onCopy} disabled={!value || busy} aria-label={`Copy ${label}`}>{busy ? <span className="spinner" /> : <Icon name="clipboard" size={16} />}</button>
  </div>;
}

function sourceLabel(video: VideoItem): string {
  if (video.sourceKind === 'remote_hls' || video.sourceProvider) {
    return `Imported from ${video.sourceProvider || 'remote HLS'}`;
  }
  return video.sourceFilename ?? 'Source file unavailable';
}

function resolutionLabel(video: VideoItem): string {
  return video.width && video.height ? `${video.width} × ${video.height}` : '—';
}

function processingStageLabel(video: VideoItem): string {
  const value = video.processingStage ?? video.status;
  const labels: Record<string, string> = {
    queued: 'Waiting for the next available worker',
    leased: 'Worker assigned',
    preparing: 'Preparing video',
    downloading: 'Downloading source',
    probing: 'Analyzing video',
    planning: 'Planning renditions',
    transcoding: 'Transcoding renditions',
    packaging: 'Packaging stream',
    thumbnail: 'Creating thumbnail',
    fallback: 'Creating MP4 fallback',
    uploading: 'Uploading processed video',
    publishing: 'Publishing video',
    validating: 'Validating playback',
    finalizing: 'Finalizing publication',
  };
  return labels[value] ?? value.replace(/[_-]+/g, ' ').replace(/^./, (letter) => letter.toUpperCase());
}

function protectionModeLabel(video: VideoItem): string {
  if (video.protectionMode === 'r2_only') return 'Playback storage';
  if (video.protectionMode === 'protected') return 'Protected storage';
  return 'Legacy automatic policy';
}

function protectionStateLabel(video: VideoItem): string {
  const state = video.protectionState;
  if (state === 'r2_only') return 'Stored for playback without an additional recovery copy.';
  if (state === 'protected_reserved') return 'The recovery copy is queued.';
  if (state === 'protected_copying') return 'Creating and verifying the recovery copy.';
  if (state === 'protected_verified') return video.protectionVerifiedAt
    ? `Recovery copy verified ${formatDate(video.protectionVerifiedAt)}.`
    : 'Recovery copy verified.';
  if (state === 'protected_retryable_failure') return 'The latest protection attempt failed and can be retried.';
  return 'Protection status is not available for this legacy release.';
}

function VideoMetadataEditor({ video, canEdit, onUpdated, onCopied }: {
  video: VideoItem;
  canEdit: boolean;
  onUpdated: (video: VideoItem) => void;
  onCopied: (message: string) => void;
}) {
  const branding = getBranding();
  const [title, setTitle] = useState(video.title);
  const [description, setDescription] = useState(video.description ?? '');
  const [draftSource, setDraftSource] = useState('');
  const [busy, setBusy] = useState<'draft' | 'save' | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    setTitle(video.title);
    setDescription(video.description ?? '');
    setDraftSource('');
    setError('');
  }, [video.id, video.title, video.description]);

  async function createDraft() {
    setBusy('draft');
    setError('');
    try {
      const result = await api.videoMetadataDraft(video.id);
      setDescription(result.description);
      setDraftSource(`${result.sourceTrack.label} (${result.sourceTrack.languageCode})`);
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Unable to create a draft from the transcript.');
    } finally {
      setBusy(null);
    }
  }

  function discardChanges() {
    setTitle(video.title);
    setDescription(video.description ?? '');
    setDraftSource('');
    setError('');
  }

  async function save() {
    const nextTitle = title.trim();
    const nextDescription = description.trim();
    if (!nextTitle) { setError('Enter a video title.'); return; }
    setBusy('save');
    setError('');
    try {
      const updated = await api.updateVideo(video.id, { title: nextTitle, description: nextDescription });
      onUpdated(updated);
      setDraftSource('');
      onCopied('Video information saved.');
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Unable to save video information.');
    } finally {
      setBusy(null);
    }
  }

  if (!canEdit) return video.description ? <div className="video-metadata-editor video-metadata-editor--readonly"><p>{video.description}</p></div> : null;
  const unchanged = title.trim() === video.title && description.trim() === (video.description ?? '');
  return <div className="video-metadata-editor">
    <div className="video-metadata-editor__fields">
      <label><span>Title</span><input value={title} maxLength={255} onChange={(event) => setTitle(event.target.value)} /></label>
      <label><span>Description</span><textarea aria-label="Video description" value={description} maxLength={5000} rows={4} onChange={(event) => setDescription(event.target.value)} placeholder="Describe the lesson or video content." /><small>{description.length.toLocaleString()} / 5,000</small></label>
    </div>
    {draftSource && <p className="video-metadata-editor__draft"><Icon name="check" size={15} />Description drafted from {draftSource}. Review it before saving.</p>}
    {error && <p className="form-alert" role="alert"><Icon name="warning" size={16} />{error}</p>}
    <div className="video-metadata-editor__actions">
      <span>This description documents the video in {branding.productName}. Page SEO remains controlled by the LMS.</span>
      <button type="button" className="button button--secondary" disabled={Boolean(busy)} onClick={() => void createDraft()}>{busy === 'draft' ? <span className="spinner" /> : <Icon name="film" size={16} />}Draft description</button>
      {!unchanged && <button type="button" className="button button--secondary" disabled={Boolean(busy)} onClick={discardChanges}><Icon name="close" size={16} />Discard changes</button>}
      <button type="button" className="button button--primary" disabled={Boolean(busy) || unchanged || !title.trim()} onClick={() => void save()}>{busy === 'save' ? <span className="spinner" /> : <Icon name="check" size={16} />}Save information</button>
    </div>
  </div>;
}

const emptyEmbedPolicy: EmbedPolicy = {
  allowedOrigins: [],
  allowedExactPaths: [],
  allowedPathPrefixes: [],
};

function splitRules(value: string): string[] {
  return [...new Set(value.split(/[\n,]+/).map((item) => item.trim()).filter(Boolean))];
}

interface EmbedWhitelistEditorProps {
  video: VideoItem;
  canEdit: boolean;
  onUpdated: (video: VideoItem) => void;
  onCopied: (message: string) => void;
}

function EmbedWhitelistEditor({ video, canEdit, onUpdated, onCopied }: EmbedWhitelistEditorProps) {
  const policy = video.embedPolicy ?? emptyEmbedPolicy;
  const [enabled, setEnabled] = useState(policy.allowedOrigins.length > 0);
  const [origins, setOrigins] = useState(policy.allowedOrigins.join('\n'));
  const [exactPaths, setExactPaths] = useState(policy.allowedExactPaths.join('\n'));
  const [prefixes, setPrefixes] = useState(policy.allowedPathPrefixes.join('\n'));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const next = video.embedPolicy ?? emptyEmbedPolicy;
    setEnabled(next.allowedOrigins.length > 0);
    setOrigins(next.allowedOrigins.join('\n'));
    setExactPaths(next.allowedExactPaths.join('\n'));
    setPrefixes(next.allowedPathPrefixes.join('\n'));
    setError('');
  }, [video.id, video.embedPolicy]);

  async function save() {
    const next: EmbedPolicy = enabled ? {
      allowedOrigins: splitRules(origins),
      allowedExactPaths: splitRules(exactPaths),
      allowedPathPrefixes: splitRules(prefixes),
    } : emptyEmbedPolicy;
    if (enabled && !next.allowedOrigins.length) {
      setError('Add at least one allowed domain, or turn the whitelist off.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const updated = await api.updateVideo(video.id, { embedPolicy: next });
      onUpdated(updated);
      onCopied(next.allowedOrigins.length ? 'Playback whitelist saved.' : 'Playback whitelist disabled.');
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Unable to save the playback whitelist.');
    } finally {
      setSaving(false);
    }
  }

  const hasPathRules = policy.allowedExactPaths.length > 0 || policy.allowedPathPrefixes.length > 0;
  if (!canEdit) {
    return <section className="video-details__card video-details__whitelist-card">
      <div className="video-details__section-heading"><div><span>Placement protection</span><h3>Playback whitelist</h3></div></div>
      <p className="video-details__explanation">{policy.allowedOrigins.length
        ? `Restricted to ${policy.allowedOrigins.join(', ')}${hasPathRules ? ' with page rules enabled.' : '.'}`
        : 'Whitelist off. Embedding is allowed on any website.'}</p>
    </section>;
  }

  return <section className="video-details__card video-details__whitelist-card">
    <div className="video-details__section-heading">
      <div><span>Placement protection</span><h3>Playback whitelist</h3></div>
      <label className="video-details__policy-switch">
        <input className="control-choice" type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />
        <span>{enabled ? 'Enabled' : 'Off'}</span>
      </label>
    </div>
    <p className="video-details__explanation">{enabled
      ? 'Allow iframe playback only on approved domains. Add optional page rules when using the embed script.'
      : 'Whitelist off. Embedding is allowed on any website. Turn it on to restrict domains or pages.'}</p>
    {enabled && <div className="video-details__policy-fields">
      <label>
        <span>Allowed domains</span>
        <textarea aria-label="Allowed domains" rows={3} value={origins} onChange={(event) => setOrigins(event.target.value)} placeholder="https://with.dwindi.com" />
        <small>One HTTPS origin per line. A normal iframe uses this domain-level rule.</small>
      </label>
      <details>
        <summary>Page rules for embed.js</summary>
        <div className="video-details__policy-paths">
          <label>
            <span>Exact page paths</span>
            <textarea aria-label="Exact page paths" rows={3} value={exactPaths} onChange={(event) => setExactPaths(event.target.value)} placeholder="/webinar/mengenal-alur-berfikir" />
            <small>Only the exact path is accepted.</small>
          </label>
          <label>
            <span>Path prefixes</span>
            <textarea aria-label="Path prefixes" rows={3} value={prefixes} onChange={(event) => setPrefixes(event.target.value)} placeholder="/webinar/" />
            <small>Each prefix must start and end with /.</small>
          </label>
        </div>
      </details>
    </div>}
    {error && <p className="form-alert" role="alert"><Icon name="warning" size={16} />{error}</p>}
    <div className="video-details__policy-footer">
      <span>{enabled ? 'Direct browser navigation to the player will be denied.' : 'Save to allow embedding on any website. Existing embed code stays the same.'}</span>
      <button type="button" className="button button--primary" disabled={saving} onClick={() => void save()}>{saving ? <span className="spinner" /> : <Icon name="shield" size={16} />}Save whitelist</button>
    </div>
  </section>;
}

interface PlayerSettingsEditorProps {
  video: VideoItem;
  canEdit: boolean;
  onUpdated: (video: VideoItem) => void;
  onCopied: (message: string) => void;
}

function sampleWatermark(text: string): string {
  return text.replaceAll('{email}','viewer@example.com').replaceAll('{code}', 'ABCD2345').replaceAll('{time}', '14:32:08');
}

function PlayerSettingsEditor({ video, canEdit, onUpdated, onCopied }: PlayerSettingsEditorProps) {
  const current = video.playerSettings ?? DEFAULT_PLAYER_SETTINGS;
  const [settings, setSettings] = useState<PlayerSettings>({ ...current });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setSettings({ ...(video.playerSettings ?? DEFAULT_PLAYER_SETTINGS) });
    setError('');
  }, [video.id, video.playerSettings]);

  function change<K extends keyof PlayerSettings>(key: K, value: PlayerSettings[K]) {
    setSettings((previous) => ({ ...previous, [key]: value,
      ...(key === 'watermarkEnabled' && value === false ? {
        viewerIdentityMode: 'disabled' as const,
        watermarkText: previous.watermarkText.includes('{email}') ? 'DS {code} · {time}' : previous.watermarkText,
      } : {}),
    }));
  }

  async function save() {
    const watermarkText = settings.watermarkText.trim().replace(/\s+/g, ' ');
    const primaryColor = settings.primaryColor.toUpperCase();
    if (!watermarkText || [...watermarkText].length > 120) {
      setError('Watermark text must contain 1–120 characters.');
      return;
    }
    if (!validPlayerColor(primaryColor)) {
      setError('Player color must use a six-digit hex value such as #5EE6BD.');
      return;
    }
    const next = { ...settings, watermarkText, primaryColor };
    setSaving(true);
    setError('');
    try {
      const updated = await api.updateVideo(video.id, { playerSettings: next });
      setSettings(next);
      onUpdated(updated);
      onCopied('Player appearance saved.');
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Unable to save player appearance.');
    } finally {
      setSaving(false);
    }
  }

  if (!canEdit) {
    return <section className="video-details__card video-details__player-card">
      <div className="video-details__section-heading"><div><span>Appearance</span><h3>Player and watermark</h3></div></div>
      <p className="video-details__explanation">Watermark {current.watermarkEnabled ? `${current.watermarkMotion}, ${current.watermarkText}` : 'off'} · Player color {current.primaryColor}</p>
    </section>;
  }

  return <section className="video-details__card video-details__player-card">
    <div className="video-details__section-heading">
      <div><span>Appearance</span><h3>Player and watermark</h3></div>
      <label className="video-details__policy-switch">
        <input className="control-choice" type="checkbox" checked={settings.watermarkEnabled} onChange={(event) => change('watermarkEnabled', event.target.checked)} />
        <span>{settings.watermarkEnabled ? 'Watermark on' : 'Watermark off'}</span>
      </label>
    </div>
    <p className="video-details__explanation">Customize this video's embed player. Existing iframe and embed-script snippets do not change.</p>
    <div className="video-details__player-fields">
      <fieldset disabled={!settings.watermarkEnabled}>
        <legend>Watermark</legend>
        <label className="video-details__player-field video-details__player-field--wide">
          <span>Text</span>
          <input aria-label="Watermark text" maxLength={120} value={settings.watermarkText} onChange={(event) => change('watermarkText', event.target.value)} />
          <small>Use <code>{'{email}'}</code> for the identified viewer, <code>{'{code}'}</code> for the session code, and <code>{'{time}'}</code> for current time.</small>
        </label>
        <label className="video-details__player-field">
          <span>Movement</span>
          <select className="control-select" aria-label="Watermark movement" value={settings.watermarkMotion} onChange={(event) => change('watermarkMotion', event.target.value as PlayerSettings['watermarkMotion'])}>
            <option value="moving">Move every 20 seconds</option>
            <option value="fixed">Stay in one position</option>
          </select>
        </label>
        <label className="video-details__player-field">
          <span>Fixed position</span>
          <select className="control-select" aria-label="Watermark fixed position" disabled={!settings.watermarkEnabled || settings.watermarkMotion !== 'fixed'} value={settings.watermarkPosition} onChange={(event) => change('watermarkPosition', event.target.value as PlayerSettings['watermarkPosition'])}>
            {WATERMARK_POSITIONS.map((position) => <option key={position.value} value={position.value}>{position.label}</option>)}
          </select>
        </label>
        <label className="video-details__player-field video-details__player-field--wide"><span>Viewer identity</span><select className="control-select" aria-label="Viewer identity mode" value={settings.viewerIdentityMode??'disabled'} onChange={event=>{const mode=event.target.value as 'disabled'|'optional'|'required';setSettings(current=>({...current,viewerIdentityMode:mode,watermarkText:mode==='disabled'&&current.watermarkText.includes('{email}')?'DS {code} · {time}':mode!=='disabled'&&!current.watermarkText.includes('{email}')?'{email} · {code}':current.watermarkText}));}}><option value="disabled">Session code only</option><option value="optional">Viewer email when available</option><option value="required">Require viewer email</option></select><small>{settings.viewerIdentityMode==='required'?'Requires a connected website and blocks anonymous public sharing. Existing playback sessions are invalidated when this policy changes.':'Connect your website in Account → Playback integrations. Optional mode uses a session-code fallback for guests.'}</small></label>
        <div className="video-details__watermark-sample">
          <span>Preview</span><strong>{settings.watermarkEnabled ? sampleWatermark(settings.watermarkText) || 'Watermark preview' : 'Watermark off'}</strong>
        </div>
      </fieldset>
      <fieldset>
        <legend>Player brand</legend>
        <label className="video-details__player-field video-details__player-field--wide">
          <span>Brand accent</span>
          <div className="video-details__color-field">
            <input className="control-color" type="color" aria-label="Choose player brand color" value={validPlayerColor(settings.primaryColor) ? settings.primaryColor : DEFAULT_PLAYER_SETTINGS.primaryColor} onChange={(event) => change('primaryColor', event.target.value.toUpperCase())} />
            <input aria-label="Player brand color" maxLength={7} value={settings.primaryColor} onChange={(event) => change('primaryColor', event.target.value)} placeholder="#5EE6BD" spellCheck={false} />
            <i aria-hidden="true" style={{ backgroundColor: validPlayerColor(settings.primaryColor) ? settings.primaryColor : 'transparent' }} />
          </div>
          <small>Applied to player accents. Text stays high-contrast, and very dark colors are lightened automatically on the black control bar.</small>
        </label>
      </fieldset>
    </div>
    {error && <p className="form-alert" role="alert"><Icon name="warning" size={16} />{error}</p>}
    <div className="video-details__policy-footer">
      <span>Saving reloads the preview above. Published embed code stays permanent.</span>
      <button type="button" className="button button--primary" disabled={saving} onClick={() => void save()}>{saving ? <span className="spinner" /> : <Icon name="play" size={16} />}Save appearance</button>
    </div>
  </section>;
}

export function VideoPreview({ video, canEdit, publicSharePagesEnabled = false, memberPublicBrandingEnabled = false, sharingReadOnly = false, folderName, onClose, onCopied, onThumbnail, onMove, onDelete, onRetry, onUpdated }: VideoPreviewProps) {
  const [tab, setTab] = useState<DetailTab>('overview');
  const [sourceState, setSourceState] = useState<{ videoId: string; sources: PlaybackSources } | null>(null);
  const [sourceError, setSourceError] = useState('');
  const [copying, setCopying] = useState<TemporaryAsset | null>(null);
  const [playerRevision, setPlayerRevision] = useState(0);
  const [failedThumbnailUrl, setFailedThumbnailUrl] = useState<string | undefined>();
  const playerFrame = useRef<HTMLIFrameElement>(null);
  const [publicEmbedOrigin, setPublicEmbedOrigin] = useState(window.location.origin);
  const sources = sourceState && sourceState.videoId === video?.id ? sourceState.sources : null;
  const embedUrl = useMemo(() => video ? new URL(`/embed/${encodeURIComponent(video.id)}`, publicEmbedOrigin).href : '', [video, publicEmbedOrigin]);
  const iframeCode = useMemo(() => video ? iframeEmbedCode(video, publicEmbedOrigin) : '', [video, publicEmbedOrigin]);
  const scriptCode = useMemo(() => video ? scriptEmbedCode(video, window.location.origin, publicEmbedOrigin) : '', [video, publicEmbedOrigin]);

  useEffect(() => {
    setPublicEmbedOrigin(window.location.origin);
    if (!video || tab !== 'embed' || !memberPublicBrandingEnabled || sharingReadOnly) return;
    let active = true;
    api.memberPublicBranding().then(value => {
      if (!active) return;
      const parsed = new URL(value.publicBaseUrl);
      if (parsed.protocol === 'https:' && !parsed.username && !parsed.password) setPublicEmbedOrigin(parsed.origin);
    }).catch(() => { /* Existing Core/canonical embeds stay usable if settings cannot be loaded. */ });
    return () => { active = false; };
  }, [video?.id, tab, memberPublicBrandingEnabled, sharingReadOnly]);

  useEffect(() => {
    setTab('overview');
    setPlayerRevision(0);
  }, [video?.id]);

  useEffect(() => {
    setFailedThumbnailUrl(undefined);
  }, [video?.thumbnailUrl]);

  useEffect(() => {
    if (!video) {
      setSourceState(null);
      setSourceError('');
      return;
    }
    setSourceError('');
    setSourceState(null);
    if (video.status !== 'ready') return;
    let active = true;
    api.playbackSources(video.id)
      .then((value) => active && setSourceState({ videoId: video.id, sources: value }))
      .catch((reason) => active && setSourceError(reason instanceof ApiError ? reason.message : 'Unable to load temporary media links.'));
    return () => { active = false; };
  }, [video?.id, video?.status, video?.updatedAt]);

  async function copy(value: string, message: string) {
    try {
      await navigator.clipboard.writeText(value);
      setSourceError('');
      onCopied(message);
    } catch {
      setSourceError('Unable to copy this value. Select it and copy it manually.');
    }
  }

  async function copyTemporary(asset: TemporaryAsset) {
    if (!video) return;
    setCopying(asset);
    try {
      const fresh = await api.playbackSources(video.id);
      setSourceState({ videoId: video.id, sources: fresh });
      const value = asset === 'hls' ? fresh.hlsUrl : asset === 'mp4' ? fresh.mp4Url : fresh.thumbnailUrl;
      if (!value) throw new Error('Asset unavailable');
      await navigator.clipboard.writeText(value);
      setSourceError('');
      onCopied(asset === 'thumbnail'
        ? 'Thumbnail URL copied.'
        : `${asset === 'hls' ? 'HLS playlist' : 'MP4 fallback'} URL copied. It expires in 90 seconds.`);
    } catch (reason) {
      setSourceError(reason instanceof ApiError ? reason.message : 'Unable to refresh or copy this media link.');
    } finally {
      setCopying(null);
    }
  }

  return <Modal
    open={Boolean(video)}
    title={video?.title ?? 'Video details'}
    description={video ? `${formatDuration(video.durationSeconds)}${video.width && video.height ? ` · ${video.width}×${video.height}` : ''}` : undefined}
    onClose={onClose}
    width="wide"
  >
    {video && <MediaDetails<DetailTab> label="Video details" value={tab} onChange={setTab} tabs={[
      { id: 'overview', label: 'Overview', icon: <Icon name="film" size={16} /> },
      ...(video.status === 'ready' ? [
        { id: 'captions' as const, label: 'Captions', icon: <span className="video-details__cc" aria-hidden="true">CC</span> },
        { id: 'chapters' as const, label: 'Chapters', icon: <Icon name="list" size={16} /> },
        { id: 'analytics' as const, label: 'Statistics', icon: <Icon name="hard-drive" size={16} /> },
        { id: 'embed' as const, label: 'Embed & links', icon: <Icon name="clipboard" size={16} /> },
        ...(publicSharePagesEnabled ? [{ id: 'share' as const, label: 'Public sharing' }] : []),
      ] : []),
    ]} preview={<div className="preview-player preview-player--embed" style={videoFrameStyle(video)}>
        {video.status === 'ready' ? <iframe
          ref={playerFrame}
          key={`${video.id}:${video.updatedAt ?? ''}:${playerRevision}`}
          src={`/embed/${encodeURIComponent(video.id)}?dashboard=1`}
          title={`${video.title} player`}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
        /> : <div className={`preview-player__state preview-player__state--${video.status}`}>
          {video.status === 'failed' ? <Icon name="warning" size={30} /> : <span className="spinner spinner--large" />}
          <strong>{video.status === 'failed' ? 'Processing failed' : processingStageLabel(video)}</strong>
          {video.status === 'failed' ? <span>{video.errorMessage || 'This video could not be processed.'}</span> : <>
            <span>{video.status === 'queued' ? 'Jobs are claimed by priority and upload time.' : `${Math.round(video.progress)}% complete`}</span>
            <span className="preview-player__progress" aria-label={`${Math.round(video.progress)}% complete`}><i style={{ width: `${video.progress}%` }} /></span>
          </>}
        </div>}
      </div>}>
      {tab === 'overview' ? <div className="media-details__overview">
        <article className="media-details__section">
          <div className="video-details__section-heading"><div><span>Video information</span><h3>File and playback details</h3></div><StatusBadge video={video} /></div>
          <VideoMetadataEditor video={video} canEdit={canEdit} onUpdated={onUpdated} onCopied={onCopied} />
          <dl className="video-details__metadata">
            <div><dt>Source</dt><dd title={sourceLabel(video)}>{sourceLabel(video)}</dd></div>
            <div><dt>Folder</dt><dd>{folderName}</dd></div>
            <div><dt>Duration</dt><dd>{formatDuration(video.durationSeconds)}</dd></div>
            <div><dt>Resolution</dt><dd>{resolutionLabel(video)}</dd></div>
            <div><dt>Tracked size</dt><dd>{formatBytes(video.importedSizeBytes ?? video.sizeBytes)}</dd></div>
            <div><dt>Added</dt><dd>{formatDate(video.createdAt)}</dd></div>
            <div><dt>Last updated</dt><dd>{formatDate(video.updatedAt)}</dd></div>
            <div><dt>Video ID</dt><dd className="video-details__id">{video.id}</dd></div>
          </dl>
          {video.status === 'ready' && <section className="video-details__thumbnail-section" aria-labelledby={`thumbnail-heading-${video.id}`}>
            <div>
              <span>Thumbnail</span>
              <h4 id={`thumbnail-heading-${video.id}`}>Library and player poster</h4>
              <p>The selected image appears in the library and before playback starts.</p>
              {canEdit && <button type="button" className="button button--secondary" onClick={() => onThumbnail(video)}><Icon name="film" size={16} />Choose thumbnail</button>}
            </div>
            <div className="video-details__thumbnail-preview" style={videoFrameStyle(video, 32)}>
              {video.thumbnailUrl && failedThumbnailUrl !== video.thumbnailUrl
                ? <img src={video.thumbnailUrl} alt={`Current thumbnail for ${video.title}`} onError={() => setFailedThumbnailUrl(video.thumbnailUrl)} />
                : <span><Icon name="film" size={24} />No thumbnail</span>}
            </div>
          </section>}
        </article>

        <aside className="media-details__side">
          <article className="media-details__section">
            <div className="video-details__section-heading"><div><span>Delivery</span><h3>Protected playback</h3></div></div>
            <p className="video-details__explanation">{video.status === 'ready' ? 'The embed player requests short-lived media tokens automatically. Use the permanent embed code for LMS pages.' : video.status === 'queued' ? 'This video is in the encoding queue. It will start when an enrolled worker becomes available.' : video.status === 'failed' ? 'Review the processing error, then retry after its cause has been corrected.' : `Current stage: ${processingStageLabel(video)}.`}</p>
            {video.status === 'ready' && <button type="button" className="button button--primary video-details__primary-action" onClick={() => setTab('embed')}><Icon name="clipboard" size={16} />Open embed &amp; links</button>}
          </article>
          {video.protectionMode && <article className="media-details__section">
            <div className="video-details__section-heading"><div><span>Storage protection</span><h3>{protectionModeLabel(video)}</h3></div></div>
            <p className={`video-details__protection-state video-details__protection-state--${video.protectionState ?? 'unknown'}`}>{protectionStateLabel(video)}</p>
            <dl className="video-details__protection-metrics">
              {video.protectionExpectedBytes !== undefined && <div><dt>Reserved estimate</dt><dd>{formatBytes(video.protectionExpectedBytes)}</dd></div>}
              {video.protectionActualBytes !== undefined && <div><dt>Verified size</dt><dd>{formatBytes(video.protectionActualBytes)}</dd></div>}
              {video.protectionAttempts !== undefined && video.protectionMode === 'protected' && <div><dt>Archive attempts</dt><dd>{video.protectionAttempts}</dd></div>}
              {video.protectionUpdatedAt && <div><dt>Last update</dt><dd>{formatDate(video.protectionUpdatedAt)}</dd></div>}
            </dl>
          </article>}
          {canEdit && <article className="media-details__section">
            <div className="video-details__section-heading"><div><span>Manage</span><h3>Video actions</h3></div></div>
            <div className="video-details__actions">
              <button type="button" onClick={() => onMove(video)}><Icon name="move" size={16} />Move to folder</button>
              {video.status === 'failed' && <button type="button" onClick={() => onRetry(video)}><Icon name="refresh" size={16} />Retry processing</button>}
              <button type="button" className="danger" onClick={() => onDelete(video)}><Icon name="trash" size={16} />Move to trash</button>
            </div>
          </article>}
        </aside>
      </div> : tab === 'captions' ? <CaptionTracksPanel
        videoId={video.id}
        durationSeconds={video.durationSeconds ?? 0}
        canEdit={canEdit}
        playerSettings={video.playerSettings ?? DEFAULT_PLAYER_SETTINGS}
        onChanged={() => setPlayerRevision((current) => current + 1)}
        onVideoUpdated={(updated) => {
          onUpdated(updated);
          setPlayerRevision((current) => current + 1);
        }}
        onSeek={(seconds) => playerFrame.current?.contentWindow?.postMessage({
          type: 'dewe-stream:seek', videoId: video.id, seconds, play: true,
        }, window.location.origin)}
      />
        : tab === 'chapters' ? <ChaptersPanel videoId={video.id} durationSeconds={video.durationSeconds ?? 0} canEdit={canEdit} />
          : tab === 'analytics' ? <VideoAnalyticsPanel videoId={video.id} />
            : tab === 'share' && publicSharePagesEnabled ? <PublicSharesPanel key={video.id} video={video} readOnly={!canEdit || sharingReadOnly} onCopied={onCopied} />
            : <div className="video-details__embed-grid">
        <section className="video-details__card">
          <div className="video-details__section-heading"><div><span>Permanent delivery</span><h3>Embed this player</h3></div></div>
          <p className="video-details__explanation">These snippets stay the same. The player refreshes expiring HLS and MP4 URLs for the viewer.</p>
          {(video.embedPolicy?.allowedExactPaths.length || video.embedPolicy?.allowedPathPrefixes.length) ? <p className="form-alert"><Icon name="warning" size={16} />Page rules require the embed script. A plain iframe only provides domain-level placement.</p> : null}
          {!(video.embedPolicy?.allowedExactPaths.length || video.embedPolicy?.allowedPathPrefixes.length)
            && <CopyCodeField label="Responsive iframe" value={iframeCode} onCopy={() => void copy(iframeCode, 'Iframe embed code copied.')} />}
          <CopyCodeField label="Embed script" value={scriptCode} onCopy={() => void copy(scriptCode, 'Embed script code copied.')} />
        </section>

        <section className="video-details__card">
          <div className="video-details__section-heading"><div><span>Video and asset links</span><h3>Copy on demand</h3></div></div>
          <p className="video-details__explanation">Media links expire after 90 seconds. Every copy action requests a fresh token.</p>
          {sourceError && <p className="form-alert" role="alert"><Icon name="warning" size={16} />{sourceError}</p>}
          {!sources && !sourceError ? <div className="video-details__links-loading"><span className="spinner spinner--large" />Preparing media links…</div> : <div className="video-details__assets">
            {!video.embedPolicy?.allowedOrigins.length
              && <AssetRow label="Embed player URL" description="Permanent player page" value={embedUrl} onCopy={() => void copy(embedUrl, 'Embed player URL copied.')} />}
            <AssetRow label="HLS playlist" description="Temporary · 90 seconds" value={sources?.hlsUrl} busy={copying === 'hls'} onCopy={() => void copyTemporary('hls')} />
            <AssetRow label="MP4 fallback" description={sources?.mp4Url ? 'Dashboard only · 90 seconds' : 'Not generated for this video'} value={sources?.mp4Url} busy={copying === 'mp4'} onCopy={() => void copyTemporary('mp4')} />
            <AssetRow label="Thumbnail" description={sources?.thumbnailUrl ? 'Public image URL' : 'Not available'} value={sources?.thumbnailUrl} busy={copying === 'thumbnail'} onCopy={() => void copyTemporary('thumbnail')} />
            <AssetRow label="Video ID" description="Stable internal identifier" value={video.id} onCopy={() => void copy(video.id, 'Video ID copied.')} />
          </div>}
        </section>
        <PlayerSettingsEditor video={video} canEdit={canEdit} onUpdated={(updated) => {
          onUpdated(updated);
          setPlayerRevision((current) => current + 1);
        }} onCopied={onCopied} />
        <EmbedWhitelistEditor video={video} canEdit={canEdit} onUpdated={(updated) => {
          onUpdated(updated);
          setPlayerRevision((current) => current + 1);
        }} onCopied={onCopied} />
      </div>}
    </MediaDetails>}
  </Modal>;
}
